import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Search, Users, Clock, LogIn, ShieldCheck } from "lucide-react";
import { api } from "../lib/api";

const fmtWhen = (iso) => {
  if (!iso) return "Never";
  const d = new Date(iso); if (isNaN(d)) return iso;
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hr${hrs === 1 ? "" : "s"} ago`;
  const days = Math.round(hrs / 24);
  if (days < 30) return `${days} day${days === 1 ? "" : "s"} ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};
const fmtDur = (s) => {
  if (!s) return "0m";
  const h = Math.floor(s / 3600), m = Math.round((s % 3600) / 60);
  return h ? `${h}h ${m}m` : `${m}m`;
};
const ROLE_STYLE = { family: "text-[#c084fc] border-[#c084fc]/40", trainer: "text-[#4ade80] border-[#4ade80]/40", admin: "text-[var(--org-accent)] border-[var(--org-accent)]/40", owner: "text-[var(--org-accent)] border-[var(--org-accent)]/40", viewer: "text-[#a0a0a0] border-white/15" };

function UserRow({ u, testId }) {
  const active = u.last_seen_at && (Date.now() - new Date(u.last_seen_at).getTime()) < 5 * 60000;
  return (
    <div className="grid grid-cols-[1fr_auto] sm:grid-cols-[1.6fr_.9fr_.7fr_.7fr] gap-x-4 gap-y-1 items-center px-4 py-2.5 border-t border-white/5" data-testid={testId}>
      <div className="min-w-0 flex items-center gap-2">
        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${active ? "bg-[#22c55e] shadow-[0_0_8px_#22c55e]" : "bg-[#3a3a3a]"}`} title={active ? "Active now" : ""} />
        <div className="min-w-0">
          <div className="text-[13px] text-white truncate">{u.name || u.email}</div>
          {u.name && <div className="text-[10px] text-[#757575] truncate font-mono">{u.email}</div>}
        </div>
        <span className={`ml-1 px-1.5 py-0.5 border text-[9px] uppercase tracking-[0.2em] shrink-0 ${ROLE_STYLE[u.role] || ROLE_STYLE.viewer}`}>{u.role || "user"}</span>
      </div>
      <div className="text-[11px] text-[#c0c0c0] flex items-center gap-1.5 justify-end sm:justify-start"><LogIn className="w-3 h-3 text-[#757575]" /> {fmtWhen(u.last_login_at)}</div>
      <div className="text-[11px] text-[#c0c0c0] font-mono hidden sm:block">{u.login_count} login{u.login_count === 1 ? "" : "s"}</div>
      <div className="text-[11px] text-[#c0c0c0] font-mono hidden sm:flex items-center gap-1.5"><Clock className="w-3 h-3 text-[#757575]" /> {fmtDur(u.active_seconds)}</div>
    </div>
  );
}

export default function UsersActivitySection() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [onlyLinked, setOnlyLinked] = useState(true);

  useEffect(() => { api.adminPlayerUsers().then(setData).catch(() => setData({ players: [], staff: [] })); }, []);

  const players = useMemo(() => {
    const list = (data?.players || []).filter(p => !onlyLinked || p.users.length > 0);
    const s = q.trim().toLowerCase();
    if (!s) return list;
    return list.filter(p => (p.name || "").toLowerCase().includes(s) || p.users.some(u => (u.email || "").toLowerCase().includes(s) || (u.name || "").toLowerCase().includes(s)));
  }, [data, q, onlyLinked]);

  const totals = useMemo(() => {
    const seen = new Map();
    (data?.players || []).forEach(p => p.users.forEach(u => seen.set(u.id, u)));
    (data?.staff || []).forEach(u => seen.set(u.id, u));
    const all = [...seen.values()];
    return { users: all.length, loggedIn: all.filter(u => u.last_login_at).length, time: all.reduce((a, u) => a + (u.active_seconds || 0), 0) };
  }, [data]);

  if (!data) return <div className="h-64 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-[var(--org-accent)]" /></div>;

  return (
    <section className="space-y-4" data-testid="users-activity-section">
      <div className="grid grid-cols-3 gap-3">
        {[["Users", totals.users, Users], ["Have logged in", totals.loggedIn, LogIn], ["Total time on site", fmtDur(totals.time), Clock]].map(([k, v, Icon]) => (
          <div key={k} className="border border-white/10 bg-[#141414] px-4 py-3">
            <div className="text-[9px] uppercase tracking-[0.3em] text-[#757575] flex items-center gap-1.5"><Icon className="w-3 h-3" /> {k}</div>
            <div className="font-heading text-3xl text-white mt-1">{v}</div>
          </div>
        ))}
      </div>

      <div className="border border-white/10 bg-[#141414]">
        <div className="flex items-center justify-between gap-3 flex-wrap px-5 py-4 border-b border-white/10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[var(--org-accent)]">Users & Activity</div>
            <div className="font-heading text-xl uppercase tracking-tight text-white">Players → Linked Accounts</div>
          </div>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0] cursor-pointer">
              <input type="checkbox" checked={onlyLinked} onChange={(e) => setOnlyLinked(e.target.checked)} className="accent-[#d8b854]" data-testid="users-only-linked" /> Only players with users
            </label>
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#757575]" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search player or email…" className="pl-8 pr-3 h-9 bg-[#0f0f0f] border border-white/10 text-xs text-white focus:outline-none w-60" data-testid="users-search" />
            </div>
          </div>
        </div>
        <div className="hidden sm:grid grid-cols-[1.6fr_.9fr_.7fr_.7fr] gap-x-4 px-4 py-2 text-[9px] uppercase tracking-[0.25em] text-[#757575] border-b border-white/10 ml-[220px]">
          <div>Account</div><div>Last login</div><div>Logins</div><div>Time on site</div>
        </div>
        {players.length === 0 && <div className="px-5 py-10 text-center text-[12px] text-[#757575]">No players match.</div>}
        {players.map(p => (
          <div key={p.id} className="flex flex-col sm:flex-row border-b border-white/10 last:border-b-0" data-testid={`users-player-${p.id}`}>
            <div className="sm:w-[220px] shrink-0 px-4 py-3 bg-[#101010] border-b sm:border-b-0 sm:border-r border-white/10">
              <Link to={`/home/${p.id}`} className="font-heading text-base uppercase text-white hover:text-[var(--org-accent)] leading-tight block truncate">{p.name}</Link>
              <div className="text-[10px] text-[#757575] uppercase tracking-[0.2em] mt-0.5">{p.league}{p.team ? ` · ${p.team}` : ""}</div>
              <div className="text-[10px] text-[#575757] mt-1">{p.users.length} {p.users.length === 1 ? "account" : "accounts"}</div>
            </div>
            <div className="flex-1 min-w-0">
              {p.users.length === 0 ? <div className="px-4 py-3 text-[11px] text-[#575757] italic">No family/trainer accounts linked</div>
                : p.users.map((u, i) => <UserRow key={u.id} u={u} testId={`users-row-${p.id}-${i}`} />)}
            </div>
          </div>
        ))}
      </div>

      {data.staff?.length > 0 && (
        <div className="border border-white/10 bg-[#141414]">
          <div className="px-5 py-3 border-b border-white/10 flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[var(--org-accent)]"><ShieldCheck className="w-3.5 h-3.5" /> Staff & Admins</div>
          {data.staff.map((u, i) => <UserRow key={u.id} u={u} testId={`users-staff-${i}`} />)}
        </div>
      )}
    </section>
  );
}
